/**
 * Public (no-auth) add-gratitude modal rendered on /u/:slug/gratitude.
 *
 * Flow: visitor fills name + email + relationship + message (+ optional image).
 * Server creates an 'awaiting' card that the wall owner will later Accept/Ignore.
 * On success we show a celebratory state + nudge the visitor to build their own wall.
 */

import { useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { HeartHandshake, Image as ImageIcon, X, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { compressImageToDataUrl } from "../utils/imageCompress";

const API = "/api";

const RELATIONSHIPS = [
  { key: "friend", label: "Friend" },
  { key: "family", label: "Family" },
  { key: "coworker", label: "Coworker" },
  { key: "mentor", label: "Mentor" },
  { key: "mentee", label: "Mentee" },
  { key: "professor", label: "Professor" },
  { key: "student", label: "Student" },
  { key: "coach", label: "Coach" },
  { key: "coachee", label: "Coachee" },
  { key: "business_partner", label: "Business Partner" },
  { key: "therapist", label: "Therapist" },
  { key: "other", label: "Other" },
];

const fireConfetti = () => {
  const colors = ["#8b5cf6", "#a78bfa", "#c4b5fd", "#f0abfc", "#fcd34d"];
  // Two bursts from the sides for a richer effect
  confetti({
    particleCount: 70,
    angle: 60,
    spread: 55,
    origin: { x: 0, y: 0.6 },
    colors,
  });
  confetti({
    particleCount: 70,
    angle: 120,
    spread: 55,
    origin: { x: 1, y: 0.6 },
    colors,
  });
};

const PublicAddGratitudeDialog = ({ open, onOpenChange, ownerName, ownerFirstName, slug, onSuccess }) => {
  const [step, setStep] = useState("compose"); // compose | success
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [relationship, setRelationship] = useState("");
  const [content, setContent] = useState("");
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { signup_url, existing_user }
  const honeypotRef = useRef(null);

  const reset = () => {
    setStep("compose");
    setName(""); setEmail(""); setRelationship(""); setContent("");
    setImageDataUrl(null); setError(null); setResult(null); setSubmitting(false);
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImageToDataUrl(file);
      setImageDataUrl(dataUrl);
    } catch {
      setError("Couldn't process that image — try a smaller one.");
    }
  };

  const submit = async () => {
    setError(null);
    if (name.trim().length < 2) return setError("Please enter your name");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setError("Please enter a valid email");
    if (!relationship) return setError("Please pick how you know them");
    if (content.trim().length === 0) return setError("Write a short message");

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/gratitude/${encodeURIComponent(slug)}/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          relationship,
          content: content.trim(),
          image_data_url: imageDataUrl,
          website: honeypotRef.current?.value || "",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong");
      setResult(data);
      setStep("success");
      setTimeout(fireConfetti, 100);
      onSuccess?.();
    } catch (e) {
      setError(e.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const closeAndReset = () => {
    onOpenChange(false);
    setTimeout(reset, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeAndReset(); else onOpenChange(true); }}>
      <DialogContent
        className="sm:max-w-lg bg-white border-0 p-0 overflow-hidden"
        data-testid="public-add-gratitude-dialog"
      >
        <AnimatePresence mode="wait">
          {step === "compose" && (
            <motion.div
              key="compose"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-6"
            >
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center">
                  <HeartHandshake className="w-4.5 h-4.5 text-violet-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900 leading-tight">
                    Share gratitude with {ownerFirstName}
                  </h2>
                  <p className="text-[11px] text-slate-500">
                    They'll decide whether to add it to their public wall.
                  </p>
                </div>
              </div>

              <div className="space-y-3.5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="pg-name" className="text-xs font-medium text-slate-600">Your name</Label>
                    <Input id="pg-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
                      placeholder="Ana García" data-testid="public-grat-name" />
                  </div>
                  <div>
                    <Label htmlFor="pg-email" className="text-xs font-medium text-slate-600">Your email</Label>
                    <Input id="pg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="ana@example.com" data-testid="public-grat-email" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-slate-600 mb-1.5 block">How do you know them?</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {RELATIONSHIPS.map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => setRelationship(r.key)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          relationship === r.key
                            ? "bg-violet-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                        data-testid={`public-grat-rel-${r.key}`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="pg-content" className="text-xs font-medium text-slate-600">Your message</Label>
                  <Textarea id="pg-content" value={content} onChange={(e) => setContent(e.target.value)}
                    placeholder="I am grateful for…" rows={4} maxLength={600}
                    className="resize-none" data-testid="public-grat-content" />
                  <div className="text-[10px] text-slate-400 text-right mt-0.5">{content.length}/600</div>
                </div>

                {imageDataUrl && (
                  <div className="relative inline-block">
                    <img src={imageDataUrl} alt="preview"
                      className="rounded-lg max-h-32 border border-slate-200" />
                    <button
                      onClick={() => setImageDataUrl(null)}
                      className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 rounded-full p-1 hover:bg-slate-100"
                    >
                      <X className="w-3 h-3 text-slate-500" />
                    </button>
                  </div>
                )}

                <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 cursor-pointer">
                  <ImageIcon className="w-3.5 h-3.5" />
                  {imageDataUrl ? "Change image" : "Add a photo (optional)"}
                  <input type="file" accept="image/*" onChange={handleImage} className="hidden" />
                </label>

                {/* Honeypot */}
                <input ref={honeypotRef} name="website" tabIndex={-1} autoComplete="off"
                  className="absolute -left-[9999px]" aria-hidden />

                {error && <div className="text-xs text-red-600 font-medium">{error}</div>}
              </div>

              <div className="flex items-center justify-end gap-2 mt-5 pt-3 border-t border-slate-100">
                <Button variant="ghost" onClick={closeAndReset} data-testid="public-grat-cancel">Cancel</Button>
                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                  data-testid="public-grat-submit"
                >
                  {submitting ? "Sending…" : "Send gratitude"}
                  {!submitting && <ArrowRight className="w-3.5 h-3.5 ml-1.5" />}
                </Button>
              </div>
            </motion.div>
          )}

          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="p-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1, rotate: [0, -10, 10, 0] }}
                transition={{ delay: 0.1, type: "spring", stiffness: 260, damping: 14 }}
                className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 mx-auto flex items-center justify-center mb-4"
              >
                <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={2.5} />
              </motion.div>
              <h2 className="text-xl font-bold text-slate-900 mb-1.5">
                Your gratitude is on its way.
              </h2>
              <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
                {ownerFirstName} will see it and decide whether to add it to their public wall.
                Either way, you just did a small good thing today.
              </p>

              <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 rounded-xl p-5 mb-5">
                <div className="flex items-center justify-center gap-1.5 mb-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  <p className="text-sm font-semibold text-violet-900">Want your own wall?</p>
                </div>
                <p className="text-xs text-slate-600 mb-4">
                  Walls like {ownerFirstName}'s take 45 seconds to set up.
                </p>
                <a
                  href={
                    result?.existing_user
                      ? "/tools/gratitude"
                      : (result?.signup_url || `/api/auth/google/login?return_url=${encodeURIComponent("/dashboard")}`)
                  }
                  className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-full px-5 py-2 text-sm font-semibold"
                  data-testid="public-grat-create-wall"
                >
                  {result?.existing_user ? "Open my wall" : "Build my gratitude wall"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>

              <Button variant="ghost" onClick={closeAndReset} className="text-slate-500" data-testid="public-grat-close">
                Close
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default PublicAddGratitudeDialog;
